import React, { useMemo, useState } from "react";
import { Alert, Container, Form, Button, Row, Col, Spinner } from "react-bootstrap";
import { CreateUserDto, turnoMap } from "../../../service/types/types";
import { UserService } from "../../../service/users.service";
import { useNavigate } from "react-router-dom";
import { formatRut, validarRut } from "../../../service/utils/constants";

type FieldKey = keyof CreateUserDto;

export default function CreateUser() {
  const [formData, setFormData] = useState<CreateUserDto>({
  rut: "",
  name: "",
  apellido_paterno: "",
  apellido_materno: "",
  fecha_nacimiento: "",
  sexo: "",
  comuna: "",
  provincia: "",
  region: "",
  estado_civil: "",
  nivel_educacion: "",
  numero_cargas: 0,
  afp: "",
  no_calzado: 0,
  talla: "",
  fecha_ingreso: "",
  sueldo: 0,
  bonos: 0,
  phone: "",
  email: "",
  direccion: "",
  cargo: "",
  jornada: "",
  contrato: "",
  prevision: "",
  turno: "",
});
  const [touched, setTouched] = useState<Partial<Record<FieldKey, boolean>>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const rutValido = useMemo(() => validarRut(formData.rut), [formData.rut]);
  const nombreValido = !!formData.name.trim();
  const telefonoValido = /^[0-9+\s-]{8,}$/.test(formData.phone || "");
  const emailValido = !formData.email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
  const turnoValido = !!formData.turno;
  const isFormValid = rutValido && nombreValido && telefonoValido && emailValido && turnoValido;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBlur = (field: FieldKey) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    if (field === "rut" && validarRut(formData.rut)) {
      setFormData((prev) => ({ ...prev, rut: formatRut(prev.rut) }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ rut: true, name: true, phone: true, email: true, turno: true });
    setError("");

    if (!isFormValid) {
      setError("Revisa los campos marcados en rojo antes de continuar.");
      return;
    }

    setSaving(true);
    try {
      await UserService.create({
        ...formData,
        rut: formatRut(formData.rut),
        organization: localStorage.getItem("organization") || undefined,
      });
      const url = `/users/view/${formatRut(formData.rut)}`;
      navigate(url);
    } catch (err) {
      console.error("Error creating user:", err);
      setError("No se pudo crear el usuario. Verifica que el RUT no esté ya registrado.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-100 min-vh-100 bg-light p-3">
      <Container fluid className="p-4 bg-white shadow rounded" >
        <h2 className="mb-4">Crear Usuario</h2>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form onSubmit={handleSubmit} className="w-100" noValidate>
          <h6 className="text-uppercase text-secondary small fw-bold mb-2">Identificación</h6>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>RUT *</Form.Label>
                <Form.Control
                  type="text"
                  name="rut"
                  value={formData.rut}
                  onChange={handleChange}
                  onBlur={handleBlur("rut")}
                  placeholder="Ej: 12345678-9"
                  isInvalid={!!touched.rut && !rutValido}
                  isValid={!!touched.rut && rutValido}
                />
                <Form.Control.Feedback type="invalid">
                  RUT inválido. Formato esperado: 12345678-9.
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Nombre *</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  onBlur={handleBlur("name")}
                  isInvalid={!!touched.name && !nombreValido}
                />
                <Form.Control.Feedback type="invalid">Ingresa un nombre.</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Apellido Paterno</Form.Label>
                <Form.Control
                  type="text"
                  name="apellido_paterno"
                  value={formData.apellido_paterno}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Apellido Materno</Form.Label>
                <Form.Control
                  type="text"
                  name="apellido_materno"
                  value={formData.apellido_materno}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Fecha Nacimiento</Form.Label>
                <Form.Control
                  type="date"
                  name="fecha_nacimiento"
                  value={formData.fecha_nacimiento}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Sexo</Form.Label>
                <Form.Select
                  name="sexo"
                  value={formData.sexo}
                  onChange={handleChange}
                >
                  <option value="">Seleccione</option>
                  <option value="M">Masculino</option>
                  <option value="F">Femenino</option>
                  <option value="O">Otro</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <h6 className="text-uppercase text-secondary small fw-bold mb-2 mt-2">Contacto</h6>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Teléfono *</Form.Label>
                <Form.Control
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  onBlur={handleBlur("phone")}
                  placeholder="+56912345678"
                  isInvalid={!!touched.phone && !telefonoValido}
                />
                <Form.Control.Feedback type="invalid">
                  Ingresa un teléfono válido (mínimo 8 dígitos).
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur("email")}
                  isInvalid={!!touched.email && !emailValido}
                />
                <Form.Control.Feedback type="invalid">Ingresa un correo válido.</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>Dirección</Form.Label>
            <Form.Control
              type="text"
              name="direccion"
              value={formData.direccion}
              onChange={handleChange}
            />
          </Form.Group>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Comuna</Form.Label>
                <Form.Control
                  type="text"
                  name="comuna"
                  value={formData.comuna}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Provincia</Form.Label>
                <Form.Control
                  type="text"
                  name="provincia"
                  value={formData.provincia}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Región</Form.Label>
                <Form.Control
                  type="text"
                  name="region"
                  value={formData.region}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <h6 className="text-uppercase text-secondary small fw-bold mb-2 mt-2">Datos Laborales</h6>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Cargo</Form.Label>
                <Form.Control
                  type="text"
                  name="cargo"
                  value={formData.cargo}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Jornada</Form.Label>
                <Form.Control
                  type="text"
                  name="jornada"
                  value={formData.jornada}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Contrato</Form.Label>
                <Form.Control
                  type="text"
                  name="contrato"
                  value={formData.contrato}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Previsión</Form.Label>
                <Form.Control
                  type="text"
                  name="prevision"
                  value={formData.prevision}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
                <Form.Group className="mb-3">
                <Form.Label>Turno *</Form.Label>
                    <Form.Select
                    name="turno"
                    value={formData.turno}
                    onChange={handleChange}
                    onBlur={handleBlur("turno")}
                    isInvalid={!!touched.turno && !turnoValido}
                    >
                    <option value="">Seleccione un turno</option>
                    {Object.entries(turnoMap).map(([key, label]) => (
                        <option key={key} value={key}>
                        {label}
                        </option>
                    ))}
                    </Form.Select>
                <Form.Control.Feedback type="invalid">Selecciona un turno.</Form.Control.Feedback>
                </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Fecha Ingreso</Form.Label>
                <Form.Control
                  type="date"
                  name="fecha_ingreso"
                  value={formData.fecha_ingreso}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <h6 className="text-uppercase text-secondary small fw-bold mb-2 mt-2">Otros Datos</h6>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Estado Civil</Form.Label>
                <Form.Control
                  type="text"
                  name="estado_civil"
                  value={formData.estado_civil}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Nivel Educación</Form.Label>
                <Form.Control
                  type="text"
                  name="nivel_educacion"
                  value={formData.nivel_educacion}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Número de Cargas</Form.Label>
                <Form.Control
                  type="number"
                  name="numero_cargas"
                  min={0}
                  value={formData.numero_cargas}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>AFP</Form.Label>
                <Form.Control
                  type="text"
                  name="afp"
                  value={formData.afp}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>N° Calzado</Form.Label>
                <Form.Control
                  type="number"
                  name="no_calzado"
                  min={0}
                  value={formData.no_calzado}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Talla</Form.Label>
                <Form.Control
                  type="text"
                  name="talla"
                  value={formData.talla}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Sueldo</Form.Label>
                <Form.Control
                  type="number"
                  name="sueldo"
                  min={0}
                  value={formData.sueldo}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Bonos</Form.Label>
                <Form.Control
                  type="number"
                  name="bonos"
                  min={0}
                  value={formData.bonos}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>
          <Button variant="outline-primary" type="submit" className="w-100 mt-3" disabled={saving}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" /> Creando...
              </>
            ) : (
              "Crear Usuario"
            )}
          </Button>
        </Form>
      </Container>
    </div>
  );
}
